import React, { Fragment, useEffect, useRef, useState } from "react";
import { Button, FormGroup, Label } from 'reactstrap';
import Select, { components } from "react-select";

const WashCareTable = (props) => {
    const { setValue, trigger, settingWashCares, settingSubWashCares, washCareRows, setWashCareRows } = props;

    // New: duplicate tracking state
    const [duplicateIndices, setDuplicateIndices] = useState([]);

    const handleWashCareRowChange = (idx, field, value) => {
        // If parent wash_care changes, reset wash_care_details so the sub-select refreshes
        if (field === 'wash_care') {
            setWashCareRows(prev => prev.map((row, i) => i === idx ? { ...row, wash_care: value, wash_care_details: [] } : row));
        } else {
            setWashCareRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
        }
    };

    const handleAddWashCareRow = () => {
        setWashCareRows(prev => ([...prev, { wash_care: '', wash_care_details: [] }]));
    };

    const handleRemoveWashCareRow = (idx) => {
        setWashCareRows(prev => prev.length > 0 ? prev.filter((_, i) => i !== idx) : prev);
    };

    useEffect(() => {
        setValue("wash_cares", [...washCareRows]);
        trigger("wash_cares");
    }, [washCareRows]);

    // New: detect duplicate rows & expose a form-level flag
    useEffect(() => {
        const keyMap = {};
        (washCareRows || []).forEach((row, idx) => {
            const key = `${row.wash_care || ''}`;
            const isEmpty = !row.wash_care && (!row.wash_care_details || row.wash_care_details.length === 0);
            if (isEmpty) return; // ignore totally empty rows
            if (!keyMap[key]) keyMap[key] = [];
            keyMap[key].push(idx);
        });
        const duplicates = new Set();
        Object.values(keyMap).forEach(arr => { if (arr.length > 1) arr.forEach(i => duplicates.add(i)); });
        const dupArray = Array.from(duplicates);
        setDuplicateIndices(dupArray);
        const hasDuplicates = dupArray.length > 0;
        if (setValue) setValue("wash_cares_has_duplicates", hasDuplicates);
        if (trigger) trigger("wash_cares_has_duplicates");
    }, [washCareRows, setValue, trigger]);

    return (
        <FormGroup>
            <Label className="form-label ai-form-heading">Wash Care</Label>
            <table className="table table-bordered table-sm align-middle">
                <thead>
                    <tr>
                        <th style={{ width: '40%' }}>Wash Care</th>
                        <th style={{ width: '40%' }}>Wash Care Details</th>
                        <th style={{ width: '20%' }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {(washCareRows || []).map((row, idx) => {
                        const isDuplicate = duplicateIndices.includes(idx);

                        const selectErrorStyles = {
                            control: (base, state) => ({
                                ...base,
                                borderColor: isDuplicate ? '#dc3545' : base.borderColor,
                                boxShadow: isDuplicate ? '0 0 0 1px rgba(220,53,69,0.12)' : base.boxShadow,
                                '&:hover': { borderColor: isDuplicate ? '#dc3545' : base.borderColor },
                            })
                        };

                        return (
                            <tr key={idx}>
                                <td>
                                    <Select
                                        placeholder="Select Wash Care"
                                        getOptionValue={option => option.id}
                                        getOptionLabel={option => option.name}
                                        options={settingWashCares}
                                        value={settingWashCares.find(opt => opt.id === row.wash_care) || null}
                                        onChange={selected => handleWashCareRowChange(idx, 'wash_care', selected ? selected.id : '')}
                                        styles={selectErrorStyles}
                                    />
                                </td>
                                <td>
                                    <Select
                                        placeholder="Select Wash Care Details"
                                        isMulti
                                        isDisabled={!row.wash_care} // disable until a wash care is selected
                                        getOptionValue={option => option.id}
                                        getOptionLabel={option => option.name}
                                        options={settingSubWashCares.filter(opt => opt.wash_care_id === row.wash_care)}
                                        value={settingSubWashCares.filter(opt => (row.wash_care_details || []).includes(opt.id))}
                                        onChange={selected => handleWashCareRowChange(idx, 'wash_care_details', selected ? selected.map(s => s.id) : [])}
                                    />
                                </td>
                                <td className="text-center">
                                    <Button 
                                        color="danger" 
                                        size="sm" 
                                        onClick={() => handleRemoveWashCareRow(idx)} 
                                    ><i className="fa fa-trash" /></Button>
                                    {isDuplicate && <div className="text-danger small mt-1">Duplicate row</div>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <Button color="success" size="sm" className="mt-2" onClick={handleAddWashCareRow}><i className="fa fa-plus" /> Add Row</Button>
        </FormGroup>
    );
};

export default WashCareTable;
